/*
 * assets/events-calendar.js — shared renderer for sections/events-calendar.liquid
 * (game filter + month grid + agenda; the grid is CSS-hidden and not built on
 * phones) and sections/events-week-strip.liquid
 * (homepage "This week at OBG" row). Vanilla JS, no jQuery, no external libs.
 * All event-derived text is inserted via createElement/textContent — never
 * innerHTML — even though titles are staff-entered from the admin app.
 *
 * Data contract: each section inlines a JSON array of obg_event metaobject
 * entries (see the two .liquid files for the exact shape). starts_at/ends_at
 * come out of Liquid's `date: "%FT%T%z"` as e.g. "2026-07-21T18:00:00-0400"
 * (shop-timezone offset, NO colon) — normalizeOffset() below inserts the
 * colon `new Date()` needs. Do not hardcode any timezone here; the offset in
 * the string is authoritative for whichever shop is running this theme.
 *
 * TIMEZONE RENDERING (I3/I5 fix): this is a physical-venue calendar, so every
 * displayed date/time must be the SHOP's wall clock, not the visitor's. A
 * `Date` object has no timezone of its own — `getFullYear()`/`getDate()`/
 * `toLocaleTimeString()` all read back components in the *viewer's* local
 * timezone, which silently shifts a late event (e.g. a 9pm PT midnight
 * prerelease) onto the wrong calendar day and shows the wrong clock time for
 * an out-of-timezone visitor. The offset-bearing string itself is already
 * shop-local, so `startYMD`/`startHM` below are sliced directly from that
 * string (pre-`new Date`) and used for every DISPLAY/grid-placement
 * decision. `Date` objects (`start`/`end`) are kept only for sorting and
 * "is this upcoming/now" instant comparisons, where viewer-vs-shop timezone
 * doesn't matter (an instant is the same instant everywhere).
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var WEEKDAY_LONG = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];
  var MONTH_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  var MONTH_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  // "-0400" -> "-04:00" so `new Date()` parses the offset correctly.
  function normalizeOffset(s) {
    if (!s) return s;
    return s.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  }

  function ymd(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  // Weekday/month/day from a "YYYY-MM-DD" string, computed the same
  // Date.UTC-as-a-neutral-calendar trick used server-side (events-recurrence/
  // app.events' localWeekday/dayHeading) — never touches the
  // runtime's local timezone, so it's correct regardless of the viewer's
  // clock.
  function ymdParts(ymdStr) {
    var bits = ymdStr.split('-');
    var y = parseInt(bits[0], 10);
    var m = parseInt(bits[1], 10);
    var d = parseInt(bits[2], 10);
    return {
      y: y,
      m: m,
      d: d,
      weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay()
    };
  }

  // "HH:MM" (24h, shop-local, sliced straight from the raw string) -> "h:MM AM/PM".
  // Fee rule (shared by the agenda line, the week-strip card and -- in Liquid --
  // sections/event-detail.liquid): staff fee text if set, else for a ticketed
  // event the linked product's LIVE price ("Sold out" when unavailable), else
  // nothing. Fee text always wins because staff wrote it on purpose; a
  // ticketed event with a blank fee shows a price that tracks Shopify.
  function feeLabel(e) {
    if (e.fee) return e.fee;
    if (e.ticketed) {
      if (e.soldOut) return 'Sold out';
      if (e.price) return e.price;
    }
    return '';
  }

  function formatTime12(hm) {
    if (!hm) return '';
    var bits = hm.split(':');
    var h = parseInt(bits[0], 10);
    var m = bits[1];
    var ampm = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + ':' + m + ' ' + ampm;
  }

  function startOfDay(d) {
    var r = new Date(d.getTime());
    r.setHours(0, 0, 0, 0);
    return r;
  }

  function parseEvents(rawJson) {
    var raw;
    try {
      raw = JSON.parse(rawJson || '[]');
    } catch (err) {
      raw = [];
    }
    if (!Array.isArray(raw)) raw = [];
    return raw
      .map(function (e) {
        var normStart = e.start ? normalizeOffset(e.start) : null;
        var normEnd = e.end ? normalizeOffset(e.end) : null;
        var start = normStart ? new Date(normStart) : null;
        var end = normEnd ? new Date(normEnd) : null;
        return {
          title: typeof e.title === 'string' ? e.title : '',
          start: start,
          // Shop-local Y-M-D / H:M, sliced from the raw offset string itself
          // (NOT derived from the `start` Date object's local getters) --
          // see the module header. The normalized string is always
          // "YYYY-MM-DDTHH:MM:SS±HH:MM", so fixed-offset slicing is safe.
          startYMD: normStart ? normStart.slice(0, 10) : null,
          startHM: normStart ? normStart.slice(11, 16) : null,
          end: end && !isNaN(end.getTime()) ? end : null,
          game: typeof e.game === 'string' ? e.game : '',
          color: typeof e.color === 'string' && e.color ? e.color : '#4a7dbd',
          status: typeof e.status === 'string' ? e.status : 'active',
          fee: typeof e.fee === 'string' ? e.fee : '',
          ticketed: !!e.ticketed,
          url: typeof e.url === 'string' && e.url ? e.url : '#',
          soldOut: !!e.soldOut,
          price: typeof e.price === 'string' ? e.price : null
        };
      })
      .filter(function (e) {
        return e.start && !isNaN(e.start.getTime()) && e.startYMD;
      })
      .sort(function (a, b) {
        return a.start - b.start;
      });
  }

  function isCancelled(e) {
    return e.status === 'cancelled';
  }

  // --- Month grid --------------------------------------------------------

  function buildMonthGrid(events, view, today, onNav, onDayClick) {
    var wrap = document.createElement('div');

    var header = document.createElement('div');
    header.className = 'evc-header';

    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'evc-nav-btn evc-nav-btn--prev';
    prevBtn.textContent = '‹ Prev';
    prevBtn.setAttribute('aria-label', 'Previous month');
    prevBtn.addEventListener('click', function () {
      onNav(-1);
    });

    var label = document.createElement('span');
    label.className = 'evc-header__label';
    label.textContent = MONTH_LONG[view.month] + ' ' + view.year;

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'evc-nav-btn evc-nav-btn--next';
    nextBtn.textContent = 'Next ›';
    nextBtn.setAttribute('aria-label', 'Next month');
    nextBtn.addEventListener('click', function () {
      onNav(1);
    });

    header.appendChild(prevBtn);
    header.appendChild(label);
    header.appendChild(nextBtn);
    wrap.appendChild(header);

    var weekdays = document.createElement('div');
    weekdays.className = 'evc-weekdays';
    WEEKDAY_SHORT.forEach(function (w) {
      var s = document.createElement('span');
      s.textContent = w;
      weekdays.appendChild(s);
    });
    wrap.appendChild(weekdays);

    var days = document.createElement('div');
    days.className = 'evc-days';

    var firstOfMonth = new Date(view.year, view.month, 1);
    var daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    var leadingBlanks = firstOfMonth.getDay();

    for (var b = 0; b < leadingBlanks; b++) {
      var blank = document.createElement('div');
      blank.className = 'evc-day evc-day--blank';
      days.appendChild(blank);
    }

    var todayYmd = ymd(today);

    for (var d = 1; d <= daysInMonth; d++) {
      var cellDate = new Date(view.year, view.month, d);
      var cellYmd = ymd(cellDate);
      var dayEvents = events.filter(function (e) {
        return e.startYMD === cellYmd;
      });

      var cellClass = 'evc-day';
      if (cellYmd === todayYmd) cellClass += ' evc-day--today';

      var cell = document.createElement(dayEvents.length ? 'button' : 'div');
      cell.className = cellClass;
      if (dayEvents.length) {
        cell.type = 'button';
        cell.addEventListener('click', (function (targetYmd) {
          return function () {
            onDayClick(targetYmd);
          };
        })(cellYmd));
      }

      var num = document.createElement('span');
      num.className = 'evc-day__num';
      num.textContent = '' + d;
      cell.appendChild(num);

      var shown = dayEvents.slice(0, 3);
      shown.forEach(function (e) {
        var chip = document.createElement('span');
        chip.className = 'evc-chip' + (isCancelled(e) ? ' evc-chip--cancelled' : '');
        chip.style.backgroundColor = e.color;
        chip.textContent = e.title;
        chip.title = e.title + (isCancelled(e) ? ' (Cancelled)' : '');
        if (isCancelled(e)) {
          var lbl = document.createElement('span');
          lbl.className = 'evc-chip__cancelled-label';
          lbl.textContent = 'Cancelled';
          chip.appendChild(lbl);
        }
        cell.appendChild(chip);
      });
      if (dayEvents.length > shown.length) {
        var more = document.createElement('span');
        more.className = 'evc-chip evc-chip--more';
        more.textContent = '+' + (dayEvents.length - shown.length) + ' more';
        cell.appendChild(more);
      }

      days.appendChild(cell);
    }

    wrap.appendChild(days);
    return wrap;
  }

  // --- Agenda --------------------------------------------------------------

  // `gameLabel` is the active filter (or '' for all) — only used for the
  // empty-state copy.
  function buildAgenda(events, today, gameLabel) {
    var wrap = document.createElement('div');
    var upcoming = events.filter(function (e) {
      var end = e.end || e.start;
      return end >= today;
    });

    if (!upcoming.length) {
      var empty = document.createElement('p');
      empty.className = 'evc-agenda-empty';
      empty.textContent = gameLabel
        ? 'No upcoming ' + gameLabel + ' events — check back soon!'
        : 'No upcoming events — check back soon!';
      wrap.appendChild(empty);
      return wrap;
    }

    var currentDayYmd = null;
    var list = null;

    upcoming.forEach(function (e) {
      var dayYmd = e.startYMD;
      if (dayYmd !== currentDayYmd) {
        currentDayYmd = dayYmd;
        var heading = document.createElement('h3');
        heading.className = 'evc-agenda-day';
        heading.id = 'evt-agenda-' + dayYmd;
        var headingParts = ymdParts(dayYmd);
        heading.textContent = WEEKDAY_LONG[headingParts.weekday] + ', ' +
          MONTH_LONG[headingParts.m - 1] + ' ' + headingParts.d;
        wrap.appendChild(heading);

        list = document.createElement('ul');
        list.className = 'evc-agenda-list';
        wrap.appendChild(list);
      }

      var row = document.createElement('li');
      row.className = 'evc-agenda-row';

      var colorBar = document.createElement('span');
      colorBar.className = 'evc-agenda-color';
      colorBar.style.backgroundColor = e.color;
      row.appendChild(colorBar);

      var body = document.createElement('div');
      body.className = 'evc-agenda-body';

      var titleLink = document.createElement('a');
      titleLink.href = e.url;
      titleLink.className = 'evc-agenda-title' + (isCancelled(e) ? ' evc-agenda-title--cancelled' : '');
      if (isCancelled(e)) {
        var s = document.createElement('s');
        s.textContent = e.title;
        titleLink.appendChild(s);
        var cancelledLabel = document.createElement('span');
        cancelledLabel.className = 'evc-agenda-cancelled-label';
        cancelledLabel.textContent = 'Cancelled';
        titleLink.appendChild(cancelledLabel);
      } else {
        titleLink.textContent = e.title;
      }
      body.appendChild(titleLink);

      var metaParts = [formatTime12(e.startHM)];
      if (e.game) metaParts.push(e.game);
      var agendaFee = feeLabel(e);
      if (agendaFee) metaParts.push(agendaFee);
      var meta = document.createElement('div');
      meta.className = 'evc-agenda-meta';
      meta.textContent = metaParts.join(' · ');
      body.appendChild(meta);

      row.appendChild(body);

      if (e.ticketed) {
        var pill = document.createElement('span');
        if (e.soldOut) {
          pill.className = 'evc-agenda-pill evc-agenda-pill--soldout';
          pill.textContent = 'Sold out';
        } else {
          pill.className = 'evc-agenda-pill';
          // Price lives on the meta line (feeLabel) -- the pill is status only.
          pill.textContent = 'Tickets';
        }
        row.appendChild(pill);
      }

      list.appendChild(row);
    });

    return wrap;
  }

  // --- Game filter ---------------------------------------------------------

  // Distinct game names present in the data window, A-Z. Built from the
  // events themselves so a game with nothing scheduled doesn't get an option.
  function uniqueGames(events) {
    var seen = {};
    var out = [];
    events.forEach(function (e) {
      if (e.game && !seen[e.game]) {
        seen[e.game] = true;
        out.push(e.game);
      }
    });
    return out.sort(function (a, b) {
      var al = a.toLowerCase();
      var bl = b.toLowerCase();
      return al < bl ? -1 : al > bl ? 1 : 0;
    });
  }

  function populateGameSelect(selectEl, games, selected) {
    selectEl.textContent = '';
    var all = document.createElement('option');
    all.value = '';
    all.textContent = 'All games';
    selectEl.appendChild(all);
    games.forEach(function (g) {
      var opt = document.createElement('option');
      opt.value = g;
      opt.textContent = g;
      selectEl.appendChild(opt);
    });
    selectEl.value = selected || '';
  }

  // ?game=<name> deep-links a filtered list (e.g. from the "Find your game"
  // copy). Unknown names fall back to "All games".
  function readGameFromUrl(games) {
    try {
      var v = new URLSearchParams(window.location.search).get('game');
      if (v && games.indexOf(v) !== -1) return v;
    } catch (err) {
      /* URLSearchParams unsupported: no deep-link */
    }
    return '';
  }

  function writeGameToUrl(game) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (game) {
        url.searchParams.set('game', game);
      } else {
        url.searchParams.delete('game');
      }
      window.history.replaceState(null, '', url.toString());
    } catch (err) {
      /* URL unsupported: selection still works, just isn't shareable */
    }
  }

  // --- Calendar section (filter + grid + agenda) ---------------------------

  function initCalendar() {
    var root = document.getElementById('events-calendar');
    if (!root) return;
    var dataEl = document.getElementById('events-cal-data');
    var gridEl = document.getElementById('events-cal-grid');
    var agendaEl = document.getElementById('events-cal-agenda');
    if (!dataEl || !gridEl || !agendaEl) return;

    var events = parseEvents(dataEl.textContent);
    var today = startOfDay(new Date());
    var view = { year: today.getFullYear(), month: today.getMonth() };
    var mql = window.matchMedia(MOBILE_QUERY);
    var selectEl = document.getElementById('events-cal-game');
    var games = uniqueGames(events);
    var selectedGame = readGameFromUrl(games);
    if (selectEl) populateGameSelect(selectEl, games, selectedGame);

    function visibleEvents() {
      if (!selectedGame) return events;
      return events.filter(function (e) {
        return e.game === selectedGame;
      });
    }

    function scrollToAgendaDay(dateYmd) {
      var target = document.getElementById('evt-agenda-' + dateYmd);
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function renderGrid() {
      gridEl.textContent = '';
      // Phones: the grid is display:none in the section CSS; skip the work.
      if (mql.matches) return;
      gridEl.appendChild(
        buildMonthGrid(visibleEvents(), view, today, function (delta) {
          view.month += delta;
          if (view.month < 0) {
            view.month = 11;
            view.year -= 1;
          } else if (view.month > 11) {
            view.month = 0;
            view.year += 1;
          }
          renderGrid();
        }, scrollToAgendaDay)
      );
    }

    function renderAgenda() {
      agendaEl.textContent = '';
      agendaEl.appendChild(buildAgenda(visibleEvents(), today, selectedGame));
    }

    if (selectEl) {
      selectEl.addEventListener('change', function () {
        selectedGame = selectEl.value;
        writeGameToUrl(selectedGame);
        renderAgenda();
        renderGrid();
      });
    }

    renderAgenda();
    renderGrid();
    // Release the pre-render min-height reservation (see the section CSS).
    root.classList.add('is-rendered');

    if (mql.addEventListener) {
      mql.addEventListener('change', renderGrid);
    } else if (mql.addListener) {
      mql.addListener(renderGrid);
    }
  }

  // --- Week strip section (homepage) ---------------------------------------

  function buildStripCard(e) {
    var card = document.createElement('div');
    card.className = 'evws-card' + (isCancelled(e) ? ' evws-card--cancelled' : '');

    var weekday = document.createElement('span');
    weekday.className = 'evws-card__weekday';
    var stripParts = ymdParts(e.startYMD);
    weekday.textContent = WEEKDAY_SHORT[stripParts.weekday] + ' ' +
      MONTH_SHORT[stripParts.m - 1] + ' ' + stripParts.d;
    card.appendChild(weekday);

    var colorBar = document.createElement('span');
    colorBar.className = 'evws-card__color';
    colorBar.style.backgroundColor = e.color;
    card.appendChild(colorBar);

    var titleLink = document.createElement('a');
    titleLink.href = e.url;
    titleLink.className = 'evws-card__title';
    if (isCancelled(e)) {
      var s = document.createElement('s');
      s.textContent = e.title;
      titleLink.appendChild(s);
    } else {
      titleLink.textContent = e.title;
    }
    card.appendChild(titleLink);

    if (isCancelled(e)) {
      var cancelledLabel = document.createElement('span');
      cancelledLabel.className = 'evws-card__cancelled-label';
      cancelledLabel.textContent = 'Cancelled';
      card.appendChild(cancelledLabel);
    }

    var metaParts = [formatTime12(e.startHM)];
    var stripFee = feeLabel(e);
    if (stripFee) {
      metaParts.push(stripFee);
    } else if (e.ticketed) {
      metaParts.push('Tickets');
    }
    var meta = document.createElement('span');
    meta.className = 'evws-card__meta';
    meta.textContent = metaParts.join(' · ');
    card.appendChild(meta);

    return card;
  }

  function initWeekStrip() {
    var root = document.getElementById('events-week-strip');
    if (!root) return;
    var dataEl = document.getElementById('events-week-strip-data');
    var rowEl = document.getElementById('events-week-strip-row');
    if (!dataEl || !rowEl) return;

    var events = parseEvents(dataEl.textContent);
    var now = new Date();
    var weekEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    var upcoming = events.filter(function (e) {
      return e.start >= now && e.start < weekEnd;
    });

    rowEl.textContent = '';
    if (!upcoming.length) {
      var empty = document.createElement('p');
      empty.className = 'events-week-strip__empty';
      empty.textContent = 'No events this week — check back soon!';
      rowEl.appendChild(empty);
      return;
    }

    upcoming.forEach(function (e) {
      rowEl.appendChild(buildStripCard(e));
    });
  }

  function init() {
    initCalendar();
    initWeekStrip();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
